import { Text } from '@chakra-ui/react';
import { useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormField from '../../../components/fields/FormField';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import {
    CreateGameAdminCommentDto,
    GameAdminCommentCategory,
} from '../../../models/game/dto/game-admin-comment.dto';
import { useCreateGameAdminComment } from '../hooks/useGameAdminComments';
import { categoryLabels } from './adminCommentConstants';

interface CreateAdminCommentModalProps {
    isOpen: boolean;
    onClose: () => void;
    gameId: number;
}

const categoryOptions = Object.values(GameAdminCommentCategory).map((cat) => ({
    value: cat,
    label: categoryLabels[cat],
}));

export default function CreateAdminCommentModal({
    isOpen,
    onClose,
    gameId,
}: CreateAdminCommentModalProps) {
    const [content, setContent] = useState('');
    const [category, setCategory] = useState<GameAdminCommentCategory | ''>('');

    const createCommentMutation = useCreateGameAdminComment(gameId);

    const handleSubmit = () => {
        if (!content.trim()) return;

        const commentData: CreateGameAdminCommentDto = {
            content: content.trim(),
            category: category || undefined,
        };

        createCommentMutation.mutate(commentData, {
            onSuccess: () => {
                setContent('');
                setCategory('');
                onClose();
            },
        });
    };

    const handleClose = () => {
        setContent('');
        setCategory('');
        onClose();
    };

    return (
        <FormModal
            isOpen={isOpen}
            onClose={handleClose}
            title={translate('admin_comments.add_comment')}
            onSubmit={handleSubmit}
            submitLabel={translate('admin_comments.add_comment')}
            cancelLabel={translate('common.cancel')}
            isSubmitting={createCommentMutation.isLoading}
            isSubmitDisabled={!content.trim()}
            size="md"
        >
            <FormStack>
                <FormField
                    type="select"
                    label={translate('admin_comments.category')}
                    value={category}
                    onChange={(value) => setCategory(value as GameAdminCommentCategory)}
                    options={categoryOptions}
                    placeholder={translate('admin_comments.select_category')}
                    helperText={translate('admin_comments.category_help')}
                />

                <FormField
                    type="textarea"
                    label={translate('admin_comments.content')}
                    value={content}
                    onChange={setContent}
                    placeholder={translate('admin_comments.content_placeholder')}
                    isRequired
                    textareaProps={{ rows: 6, resize: 'vertical' }}
                />

                {category === GameAdminCommentCategory.PAYMENT_EXTERNAL && (
                    <Text fontSize="sm" color="orange.600" bg="orange.50" p={3} borderRadius="md">
                        💡 {translate('admin_comments.payment_external_tip')}
                    </Text>
                )}
            </FormStack>
        </FormModal>
    );
}
